import { MarketResearch, ResearchedProduct } from "@/types/MarketResearch"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getDateFromId, getFormatDate } from "@/utils/date"
import TableProductMarketResearch from "./components/TableProductMarketResearch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useProductMarketResearchSpreadsSheet } from "./useProductMarketResearchSpreadsSheets"
import ButtonWithTooltip from "@/components/ButtonWithTooltip"
import { Button } from "@/components/ui/button"
import { ArrowDownToLine, Map as MapIcon } from "lucide-react"
import { TypographyXS } from "@/components/ui/typography"
import useDisclosure from "@/hooks/useDisclosure"
import { useImageResearchedProductQuery } from "@/queries/useImageResearchedProductQuery"
import ViewImageDialog from "../ViewImageDialog"
import { useResearchCoverageProductsNotSearchedQuery } from "@/queries/useResearchCoverageProductsNotSearched"

interface props {
    marketResearch: MarketResearch,
    dataResearchedProducts: ResearchedProduct[]
}

export interface structData  {
    cycle: string
    endDate: Date
    initialDate: Date
    dataResearchedProducts: ResearchedProduct[]

}

const AdmLayout = ({ marketResearch, dataResearchedProducts } : props) => {   
    const navigate = useNavigate()
    const [dataResearchedProduct, setDataResearchedProduct] = useState<structData[]>([])
    const [activeCycle, setActiveCycle] = useState<string>("")
    const { onDownload } = useProductMarketResearchSpreadsSheet() 
    const [srcImage, setSrcImage] = useState<string>('')
    const [keyImage, setkeyImage] = useState<string>('')
    const [status, setStatus] = useState(false)

    const { data: dataImage } = useImageResearchedProductQuery(keyImage, status)
    const { data: dataProductsNotSearched } = useResearchCoverageProductsNotSearchedQuery(marketResearch.SK_PESQUISA)

    const {
        isOpen: isOpenViewImage,
        onClose: onCloseViewImage,
        onOpen: onOpenViewImage
    } = useDisclosure()
 
    const handleViewImage = (key: string) => {
        setkeyImage(key)
        setStatus(true)
    }

    useEffect(() => {
        if(dataImage) {
            setSrcImage(dataImage.url)
            onOpenViewImage()
        } 
    }, [dataImage, keyImage]);

    const generateCycle = ( p_InitialDate: number, p_EndDate: number, typeCycle: string) => {
        const initialDate = new Date(getDateFromId(p_InitialDate))
        const endDate = new Date(getDateFromId(p_EndDate))

        const cycles: {
            countCycle: string
            initialDate: Date
            endDate: Date
        }[] = [] 

        let dateInitialCycle = new Date(initialDate)
        let count = 1

        const addDays = (date: Date, days: number) => {
            const newDate = new Date(date)
            newDate.setDate(newDate.getDate() + days)
            return newDate
        } 
 
        const weekend = (date: Date) => {
            const dayWeek = date.getDay()  
            const daysForSaturday = 6 - dayWeek
            return addDays(date, daysForSaturday)
        }

        while (dateInitialCycle <= endDate) {
            let endCycle: Date

            switch (typeCycle) {
            case 'Diário':
                endCycle = dateInitialCycle
                break

            case 'Semanal':
                endCycle = weekend(dateInitialCycle)
                break

            case 'Quinzenal':
                endCycle = addDays(dateInitialCycle, 14)
                break

            case 'Mensal':
                endCycle = new Date(dateInitialCycle)
                endCycle.setMonth(endCycle.getMonth() + 1)
                endCycle.setDate(endCycle.getDate() - 1)
                break

            case 'Período':
                endCycle = new Date(endDate)
                break

            default:
                endCycle = new Date(endDate)
            }
 
            if (endCycle > endDate) {
                endCycle = new Date(endDate)
            }

            cycles.push({
                countCycle: count.toString(),
                initialDate: dateInitialCycle,
                endDate: endCycle
            })

            count++

            if (typeCycle === 'Período') break
 
            dateInitialCycle = addDays(endCycle, 1)
        }

        return cycles
    }

    useEffect(() => {
        if(marketResearch) {
            const cycles = generateCycle(marketResearch.SK_TEMPO_INICIO, marketResearch.SK_TEMPO_FIM, marketResearch.DD_TIPO_CICLO) 
            
            const data =  dataResearchedProducts.map(item=> {
                const cycle = cycles.find(cycle => new Date(item.CREATED_AT) >= cycle.initialDate && new Date(item.CREATED_AT) <= cycle.endDate)?.countCycle 
                return {
                    ...item,
                    DD_CICLO: cycle?.toString() || cycles[cycles.length - 1].countCycle
                }
            })
            
            setDataResearchedProduct(cycles.map( cycle => {
                return {
                    cycle: cycle.countCycle,
                    endDate: cycle.endDate,
                    initialDate: cycle.initialDate,
                    dataResearchedProducts: data.filter(item => item.DD_CICLO == cycle.countCycle) 
                }
            }))
        } 
    }, [marketResearch, dataResearchedProducts]) 

    const defaultCycle = useMemo(() => {
        if (dataResearchedProduct.length === 0) return ""
        const withData = [...dataResearchedProduct]
            .reverse()
            .find((c) => c.dataResearchedProducts.length > 0)
        return withData?.cycle ?? dataResearchedProduct[0].cycle
    }, [dataResearchedProduct])

    useEffect(() => {
        if (!defaultCycle) return
        setActiveCycle((prev) =>
            prev && dataResearchedProduct.some((c) => c.cycle === prev)
                ? prev
                : defaultCycle
        )
    }, [defaultCycle, dataResearchedProduct])

    const handleDownload = () => {
        dataResearchedProduct && dataProductsNotSearched && onDownload(dataResearchedProduct, dataProductsNotSearched)
    } 

    return (
        <div className="pt-4"> 
            <div className="flex justify-between items-center mb-2">
                <div>
                    <TypographyXS className="text-sm font-medium text-gray-900 flex gap-2"> 
                       Ciclo aplicado: {marketResearch.DD_TIPO_CICLO}
                    </TypographyXS>
                </div>
                <ButtonWithTooltip
                    tooltipContent={<p className="text-white">Download</p>}
                    onClick={() => handleDownload()}
                    variant="outline"
                >
                    <ArrowDownToLine size={18} />
                </ButtonWithTooltip>
            </div>
            {dataResearchedProduct.length > 0 && activeCycle && (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-5 flex flex-col">
                        <TypographyXS className="text-sm font-semibold text-gray-900">
                            Produtos por ciclo
                        </TypographyXS>
                        <TypographyXS className="text-xs text-gray-500 mt-0.5">
                            Selecione o ciclo para ver as coletas do período.
                        </TypographyXS>
                    </div>

                    <Tabs
                        value={activeCycle}
                        onValueChange={setActiveCycle}
                        className="w-full"
                    >
                        <div className="border-b border-gray-100 px-3 sm:px-4 pt-3 pb-0 overflow-x-auto">
                            <TabsList className="mb-0 inline-flex h-auto min-h-9 w-max max-w-full flex-wrap justify-start gap-1.5 rounded-lg bg-muted/60 p-1.5 sm:flex-nowrap">
                                {dataResearchedProduct.map((item) => {
                                    const count = item.dataResearchedProducts.length
                                    return (
                                        <div
                                            key={item.cycle}
                                            className="inline-flex shrink-0 items-stretch gap-0.5 rounded-lg bg-background/80 p-0.5 ring-1 ring-border/60"
                                        >
                                            <TabsTrigger
                                                value={item.cycle}
                                                className="shrink-0 gap-2 px-3 py-2 text-left data-[state=active]:shadow-sm"
                                            >
                                                <span className="flex flex-col items-start gap-0.5">
                                                    <span className="text-xs font-semibold leading-none">
                                                        Ciclo {item.cycle}
                                                    </span>
                                                    <span className="max-w-[200px] truncate text-[11px] font-normal text-muted-foreground sm:max-w-[240px]">
                                                        {getFormatDate(item.initialDate)} —{" "}
                                                        {getFormatDate(item.endDate)}
                                                    </span>
                                                </span>
                                                <span
                                                    className={
                                                        "ml-1 rounded-full px-2 py-0.5 text-[10px] font-medium leading-none " +
                                                        (count > 0
                                                            ? "bg-primary/15 text-primary"
                                                            : "bg-muted text-muted-foreground")
                                                    }
                                                >
                                                    {count}
                                                </span>
                                            </TabsTrigger>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-auto min-w-9 shrink-0 rounded-md"
                                                title="Abrir mapa deste ciclo"
                                                aria-label={`Abrir mapa do ciclo ${item.cycle}`}
                                                onClick={() =>
                                                    navigate(
                                                        `/market-research/map?search=${marketResearch.SK_PESQUISA}`,
                                                        {
                                                            state: {
                                                                researchedProducts:
                                                                    item.dataResearchedProducts,
                                                            },
                                                        }
                                                    )
                                                }
                                            >
                                                <MapIcon className="size-4" />
                                            </Button>
                                        </div>
                                    )
                                })}
                            </TabsList>
                        </div>

                        {dataResearchedProduct.map((item) => (
                            <TabsContent
                                key={item.cycle}
                                value={item.cycle}
                                className="m-0 px-3 pb-4 pt-3 sm:px-5 sm:pb-5 h-[65vh]"
                            >
                                <div className="space-y-2 h-full">
                                    <TypographyXS className="text-xs text-gray-500">
                                        Período:{" "}
                                        <span className="font-medium text-gray-700">
                                            {getFormatDate(item.initialDate)} —{" "}
                                            {getFormatDate(item.endDate)}
                                        </span>
                                        {" · "}
                                        {item.dataResearchedProducts.length} registro(s)
                                    </TypographyXS>
                                    <div className="flex flex-col h-full overflow-auto py-2">
                                        <TableProductMarketResearch
                                            dataResearchedProduct={
                                                item.dataResearchedProducts
                                            }
                                            handleViewImage={handleViewImage}
                                        />
                                    </div>
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </div>
            )}

            {srcImage && (
                <ViewImageDialog 
                   isOpen={isOpenViewImage} 
                   onClose={onCloseViewImage} 
                   src={srcImage}
               />
            )}
            
        </div>
    )
}

export default AdmLayout
